import { useState } from 'react'

import { Edit02Icon, Mail01Icon } from '@hugeicons/react'
import { toast } from 'sonner'

import { env } from '@/env'
import { useResourceRecordContext, useSessionContext } from '@/shared/hooks'
import { api } from '@/shared/lib/api'
import { Button, Card } from '@/shared/ui'
import { Avatar, useAvatarContext } from '@/shared/ui/Avatar'
import { alert } from '@/shared/ui/dialog'
import { InputGroup } from '@/shared/ui/Form'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'

import { BottomSheetWrapper } from '../BottomSheetWrapper'
import { ACCOUNT_PATHS } from '../config'
import { DigitalProductMyProfileEmails } from '../Fields/Emails'
import { DigitalProductMyProfileFields } from '../Fields/Profile'

const isDev = env.NODE_ENV === 'development'

export const ProfilePanel = () => {
  const { refreshSession } = useSessionContext()
  const { record: user, Form, isSubmitting } = useResourceRecordContext()
  const { avatar } = useAvatarContext()
  const [openEditEmail, setOpenEditEmail] = useState(false)

  const handleUpdateEmail = data => {
    api
      .put('/user/update_email', {
        record: {
          email: data?.email
        }
      })
      .then(() => {
        toast.success('E-mail atualizado!', {
          description:
            'Você precisa confirmar o novo e-mail na sua caixa de entrada.'
        })
        setOpenEditEmail(false)
      })
      .catch(error => {
        console.error(error)

        void alert({
          variant: 'destructive',
          title: 'Erro ao atualizar e-mail',
          description:
            error?.response?.data?.message || 'Tente novamente mais tarde.',
          confirmLabel: 'Fechar'
        })
      })
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <Card.Body className="p-4 sm:p-5">
          <Form onSave={refreshSession}>
            <DigitalProductMyProfileFields currentName={user?.name || ''} />
          </Form>

          {/* Fora de dev o participante não vê nem edita o próprio e-mail.
              Decisão do tech lead, mantida desta refatoração — ver
              "Perguntas em Aberto" no prd da feature. */}
          {isDev && (
            <div className="mt-6 flex w-full flex-col border-t border-gray-100 pt-6">
              <Label className="mb-2 block">E-mail</Label>
              <InputGroup>
                <InputGroup.Addon tone="brand">
                  <Mail01Icon variant="stroke" size={18} />
                </InputGroup.Addon>
                <Input
                  type="text"
                  className="rounded-none border-0 bg-transparent focus-visible:ring-0"
                  value={user?.email || ''}
                  disabled
                  readOnly
                />
                <InputGroup.Addon
                  interactive
                  onClick={() => setOpenEditEmail(true)}
                  aria-label="Editar e-mail"
                >
                  <Edit02Icon variant="stroke" size={18} />
                </InputGroup.Addon>
              </InputGroup>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card>
        {/* Duas colunas: avatar e, ao lado, a coluna de texto com a ação
            empilhada dentro dela. A ação não pode virar uma terceira coluna —
            a 375px sobrariam ~21 caracteres para a descrição.

            Com o frame de 96px a coluna de texto fica em ~207px, ou ~27
            caracteres a 14px: abaixo do piso de 32 do ACCESSIBILITY.md §2.4.
            O tamanho do avatar é decisão de design assumida; a copy foi
            encurtada para caber em duas linhas nessa medida. Se a descrição
            crescer, ela precisa descer para a largura inteira. */}
        <Card.Body className="flex items-start gap-4 p-4 sm:p-5">
          <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand/10 sm:h-28 sm:w-28">
            <Avatar className="h-full w-full" config={avatar || {}} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
            <span className="text-base font-semibold text-gray-900">
              Seu avatar
            </span>
            <p className="m-0 text-sm text-gray-700">
              Personalize como você aparece no evento.
            </p>
            <Button
              href={ACCOUNT_PATHS.avatar}
              variant="primary"
              size="sm"
              showTrailingIcon={false}
              leadingIcon={<Edit02Icon variant="stroke" size={16} />}
              className="mt-1"
            >
              Editar avatar
            </Button>
          </div>
        </Card.Body>
      </Card>

      <BottomSheetWrapper
        open={openEditEmail}
        onClose={() => setOpenEditEmail(false)}
        title="Alterar e-mail"
      >
        <Form
          validationSchema={DigitalProductMyProfileEmails.validationSchema()}
          onSubmit={handleUpdateEmail}
        >
          <DigitalProductMyProfileEmails />
          <div className="mt-4 flex gap-2">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={isSubmitting}
              showTrailingIcon={false}
            >
              Atualizar e-mail
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setOpenEditEmail(false)}
            >
              Cancelar
            </Button>
          </div>
        </Form>
      </BottomSheetWrapper>
    </div>
  )
}
